// Data access for the (hidden) "Map of the Week" feature.
//
// Three collections:
//   motwConfig/settings      — the schedule, editable in the admin panel
//   motwWeeks/{YYYY-MM-DD}   — the course picked for that event day
//   motwPlayers/{uid}        — players who opted in + their CS2KZ SteamID
//
// One course per week, two submission windows on the event day (a daytime one
// and a night one) so people in different timezones get a fair shot. A player
// competes in ONE window only: the first one they set a time in. Runs in the
// other window don't count, so nobody gets two bites at the same course.
//
// The course is picked DETERMINISTICALLY from the event date, so any number of
// concurrent first-visitors compute the same pick and the idempotent setDoc
// converges. There is intentionally no cron: the first visitor materialises the
// week doc. An admin can override the pick for any week.
//
// The resolved window instants are FROZEN into the week doc when it is created.
// Changing the schedule later must not silently move a window that players
// already ran in.

import { collection, doc, getDoc, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { fetchApprovedMaps, KZ_MODES } from '../lib/cs2kz';
import { DEFAULT_TIME_ZONE, currentEventDate, zonedTimeToUtc } from '../lib/kzTime';

const CONFIG_DOC = 'settings';

/** Shipped defaults — Sunday, an afternoon window and a night window, 1h each. */
export const DEFAULT_CONFIG = {
  enabled: true,
  dayOfWeek: 0,
  timeZone: DEFAULT_TIME_ZONE,
  points: [3, 2, 1],
  sessions: [
    { key: 'day', label: 'Day', startTime: '14:00', durationMinutes: 60 },
    { key: 'night', label: 'Night', startTime: '21:00', durationMinutes: 60 },
  ],
};

/** Read the schedule, falling back to the defaults when nothing is saved yet. */
export const getConfig = async () => {
  const snap = await getDoc(doc(db, 'motwConfig', CONFIG_DOC));
  if (!snap.exists()) return { ...DEFAULT_CONFIG };
  const data = snap.data();
  return {
    ...DEFAULT_CONFIG,
    ...data,
    sessions:
      Array.isArray(data.sessions) && data.sessions.length
        ? data.sessions
        : DEFAULT_CONFIG.sessions,
    points: Array.isArray(data.points) && data.points.length ? data.points : DEFAULT_CONFIG.points,
  };
};

/**
 * Save the schedule. Field set MUST stay in sync with the motwConfig rule in
 * firestore.rules.
 */
export const saveConfig = async (config) => {
  await setDoc(doc(db, 'motwConfig', CONFIG_DOC), {
    enabled: Boolean(config.enabled),
    dayOfWeek: Number(config.dayOfWeek),
    timeZone: config.timeZone,
    points: config.points.map(Number),
    sessions: config.sessions.map((session) => ({
      key: session.key,
      label: session.label,
      startTime: session.startTime,
      durationMinutes: Number(session.durationMinutes),
    })),
    updatedAt: Date.now(),
  });
};

/**
 * Turn the schedule into absolute windows for one event date.
 * @returns {Array<{key,label,startsAt,endsAt}>} epoch ms, in schedule order
 */
export const resolveSessions = (config, eventDate) =>
  config.sessions.map((session) => {
    const startsAt = zonedTimeToUtc(eventDate, session.startTime, config.timeZone);
    return {
      key: session.key,
      label: session.label,
      startsAt,
      endsAt: startsAt + Number(session.durationMinutes) * 60000,
    };
  });

/** The event date players are currently looking at (YYYY-MM-DD). */
export const eventDateFor = (config, now = Date.now()) =>
  currentEventDate(Number(config.dayOfWeek), config.timeZone, now);

// Stable, dependency-free 32-bit string hash (FNV-1a + an avalanche finalizer).
// The finalizer matters: consecutive event dates differ by a character or two,
// and without it the seed moved in step, so the pick just walked down the map
// list in order and was trivially predictable.
const hashString = (str) => {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 2246822507);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 3266489909);
  hash ^= hash >>> 16;
  return hash >>> 0;
};

/** Per-mode ranked state + tier for one course, keyed by mode id. */
const modeInfo = (course) =>
  KZ_MODES.reduce((acc, mode) => {
    const filter = course?.filters?.[mode.id];
    acc[mode.id] = {
      ranked: filter?.state === 'ranked',
      tier: filter?.nub_tier ?? null,
    };
    return acc;
  }, {});

/**
 * Every (map, course) pair playable in at least one mode. A course that is
 * unranked in both is skipped entirely — no one can set a ranked time on it.
 */
export const eligibleCourses = (maps) =>
  maps.flatMap((map) =>
    (map.courses || [])
      // courseIndex is the course's 1-based position in the map's list — the
      // screenshot repo names images by that number.
      .map((course, i) => ({ map, course, courseIndex: i + 1, modes: modeInfo(course) }))
      .filter((entry) => Object.values(entry.modes).some((info) => info.ranked))
  );

/** The course fields of a week doc, from an eligibleCourses() entry. */
const courseFields = (entry) => ({
  mapId: entry.map.id,
  mapName: entry.map.name,
  workshopId: entry.map.workshop_id,
  courseName: entry.course.name,
  courseIndex: entry.courseIndex,
  modes: entry.modes,
});

/** Deterministically pick the week's course from the approved-map list. */
const pickForDate = (eventDate, maps) => {
  const candidates = eligibleCourses(maps);
  if (candidates.length === 0) return null;
  return candidates[hashString(eventDate) % candidates.length];
};

// Week docs written before a schema change are treated as stale and re-picked
// rather than rendering a board that can't be filtered or timed.
const isCurrentShape = (data) =>
  Boolean(data?.courseName && data?.modes && data?.courseIndex && data?.sessions?.length);

/**
 * Return the week doc for an event date, creating it on first visit.
 * @returns {Promise<object|null>} the week doc, or null if the CS2KZ map list
 *   yielded no eligible course
 */
export const getOrCreateWeek = async (config, eventDate) => {
  const ref = doc(db, 'motwWeeks', eventDate);
  const snap = await getDoc(ref);
  if (snap.exists() && isCurrentShape(snap.data())) return { id: snap.id, ...snap.data() };

  const maps = await fetchApprovedMaps();
  const picked = pickForDate(eventDate, maps);
  if (!picked) return null;

  // Field set here MUST stay in sync with the motwWeeks rule in firestore.rules.
  const weekDoc = {
    date: eventDate,
    ...courseFields(picked),
    sessions: resolveSessions(config, eventDate),
    overridden: false,
    createdAt: Date.now(),
  };
  await setDoc(ref, weekDoc);
  return { id: eventDate, ...weekDoc };
};

/** Read a week doc without creating it. */
export const getWeek = async (eventDate) => {
  const snap = await getDoc(doc(db, 'motwWeeks', eventDate));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/**
 * Admin override: replace a week's course, keeping its windows.
 * @param {object} entry - an eligibleCourses() entry
 */
export const setWeekCourse = async (config, eventDate, entry) => {
  const existing = await getWeek(eventDate);
  const weekDoc = {
    date: eventDate,
    ...courseFields(entry),
    sessions: existing?.sessions?.length ? existing.sessions : resolveSessions(config, eventDate),
    overridden: true,
    createdAt: existing?.createdAt ?? Date.now(),
  };
  await setDoc(doc(db, 'motwWeeks', eventDate), weekDoc);
  return { id: eventDate, ...weekDoc };
};

/** Re-apply the current schedule to a week whose windows haven't started. */
export const resetWeekSessions = async (config, eventDate) => {
  const existing = await getWeek(eventDate);
  if (!existing) return null;
  const weekDoc = { ...existing, sessions: resolveSessions(config, eventDate) };
  delete weekDoc.id;
  await setDoc(doc(db, 'motwWeeks', eventDate), weekDoc);
  return { id: eventDate, ...weekDoc };
};

/** All players who have opted in. */
export const listMotwPlayers = async () => {
  const snap = await getDocs(collection(db, 'motwPlayers'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

/**
 * The current user's opt-in doc, or null.
 *
 * Falls back to the pre-rename `motdPlayers` row and carries it over, so people
 * who linked their Steam profile under Map of the Day don't have to do it again.
 */
export const getMyMotwPlayer = async (uid) => {
  if (!uid) return null;
  const snap = await getDoc(doc(db, 'motwPlayers', uid));
  if (snap.exists()) return { id: snap.id, ...snap.data() };

  const legacy = await getDoc(doc(db, 'motdPlayers', uid));
  if (!legacy.exists()) return null;
  const carried = legacy.data();
  await setDoc(doc(db, 'motwPlayers', uid), carried, { merge: true });
  return { id: uid, ...carried };
};

/**
 * Opt the current user in (or update their linked SteamID).
 * @param {string} uid
 * @param {{ steamId: string, username: string, kzPlayerName?: string }} data
 */
export const joinMotw = async (uid, { steamId, username, kzPlayerName }) => {
  await setDoc(
    doc(db, 'motwPlayers', uid),
    {
      uid,
      steamId,
      username: username || 'Unknown',
      kzPlayerName: kzPlayerName || null,
      linkedAt: Date.now(),
    },
    { merge: true }
  );
};

/** Opt the current user out. */
export const leaveMotw = async (uid) => {
  await deleteDoc(doc(db, 'motwPlayers', uid));
};
